/* eslint-disable no-template-curly-in-string */
import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { NoticeLoading } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { useHistory, useParams } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import PersonMasonryLayout from '@fs/tree-person-masonry-layout'
import PersonPageBanner from '@fs/tree-person-page-banner'
import {
  ActivitiesCard,
  CreateAccountCard,
  FamilyCard,
  LifeOfCard,
  MemoriesCard,
  NameMeaningCard,
  SourcesCard,
  TimelineCard,
  WorldEventsCard,
  StoryCard,
} from '@fs/tree-person-info-cards'
import { useDataContext } from '../../services/data-service'
import usePerson from '../../services/usePerson'
import useMemories from '../../services/useMemories'
import useNameMeanings from '../../services/useNameMeanings'
import useLifeOf from '../../services/useLifeOf'
import useFamilies from '../../services/useFamilies'
import useSources from '../../services/useSources'
import useWorldEvents from '../../services/useWorldEvents'
import useTimeline from '../../services/useTimeline'
import useStoryCard from '../../services/useStoryCard'
import NotFoundError from '../errors/NotFoundError'
import { getExternalBaseUrl } from '../../services/url-service'
import { TimelineItems } from '../../types/data-types'
import GoneError from '../errors/GoneError'
import MovedError from '../errors/MovedError'
import NotPublished from '../NotPublished'
import PageHead from '../PageHead'
import AncestorPageFooter from './AncestorPageFooter'

const centeredCss = css`
  max-width: 1462px;
  margin: 16px auto;
  padding: 0 16px;
`

const messageCss = css`
  display: grid;
  grid-template-columns: min-content max-content;
  align-items: center;
  margin: 40px auto 0;
  grid-column-gap: 12px;
  width: max-content;
`

export function displayTimeline(timelineItems: TimelineItems | undefined): boolean {
  let hasValidParentStart = false
  let hasValidSpouseStart = false

  timelineItems?.parentFamily?.forEach((item) => {
    if (item.start && item.start > 0) hasValidParentStart = true
  })
  timelineItems?.spouseFamily?.forEach((item) => {
    if (item.start && item.start > 0) hasValidSpouseStart = true
  })

  const hasValidParentFamily = (timelineItems?.parentFamily?.length || 0) > 0 && hasValidParentStart
  const hasValidSpouseFamily = (timelineItems?.spouseFamily?.length || 0) > 0 && hasValidSpouseStart

  return hasValidParentFamily || hasValidSpouseFamily
}

export default function AncestorPage(): JSX.Element {
  const [t] = useTranslation()
  const dataContext = useDataContext()
  const { store: dataState } = dataContext
  const person = usePerson()
  const memories = useMemories()
  const nameMeanings = useNameMeanings()
  const lifeOf = useLifeOf()
  const families = useFamilies()
  const worldEvents = useWorldEvents()
  const sources = useSources()
  const popularStory = useStoryCard()
  const timelineItems = useTimeline()
  const { signedIn } = useUser()
  const showRelationship = signedIn
  const canDisplayTimeline = displayTimeline(timelineItems)
  const history = useHistory()
  const { nameAndLifespan } = useParams()
  const linkPage = 'Person'
  const { CF } = dataState

  const baseUrl = getExternalBaseUrl()

  usePageView({ page_detail: linkPage })

  const encodedNameAndLifespan = person?.url.substring(person?.url.lastIndexOf('/') + 1).replace(/\+/g, ' ')
  const decodedNameAndLifespan = decodeURIComponent(encodedNameAndLifespan || '')
  // Route /:pid to /:pid/:nameAndLifespan, but only if not server-side rendering
  const nameLifespanNotInURL = decodeURIComponent(nameAndLifespan) !== decodedNameAndLifespan

  if (person?.url && (!nameAndLifespan || nameLifespanNotInURL) && !process.env.SSR) {
    history.replace(
      `${window?.location?.hostname === 'localhost' ? `/${i18n.language}` : ''}/${person.id}/${encodeURIComponent(
        decodedNameAndLifespan
      )}`
    )
    return <></>
  }

  if (dataState.loading)
    return (
      <div css={messageCss} data-testid="loading">
        <NoticeLoading size="lg" />
      </div>
    )

  if (dataState.notFound) return <NotFoundError />

  if (dataState.gone) return <GoneError />

  if (dataState.movedTo) return <MovedError location={dataState.movedTo} />

  if (dataState.error) throw dataState.error // Let ErrorBoundary catch this error

  if (!person) return <></> // Let TS compiler know person is not undefined below

  const pageTitle = t('ancestors.person.meta-data.title', '{personName} ({lifespan})', {
    personName: person.name,
    lifespan: person.lifespan,
  })
  const pageDescription = t(
    'ancestors.person.meta-data.description',
    'Discover life events, stories and photos about {personName} ({lifespan}) of {address}.',
    {
      personName: person.name,
      lifespan: person.lifespan,
      address: person?.birth?.details?.place?.localizedText || '',
    }
  )

  const socialShareTitle = t('ancestors.person.meta-data.social-share-title', '{personName} ({lifespan})', {
    personName: person.name,
    lifespan: person.lifespan,
  })
  const socialShareDescription = t(
    'ancestors.person.meta-data.social-share-description',
    'Discover life events, stories and photos about {personName} ({lifespan}) of {address}.',
    {
      personName: person.name,
      lifespan: person.lifespan,
      address: person?.birth?.details?.place?.localizedText || '',
    }
  )

  const image = person.portraitUrl ? { url: person.portraitUrl, alt: pageTitle, width: 200, height: 200 } : undefined
  const analytics = { link_app: 'tree-ancestors-r9', link_page: linkPage }

  return (
    <>
      <script>window.ANCESTORS_URL = &apos;{person.url}&apos;; </script>
      <PageHead
        title={pageTitle}
        description={pageDescription}
        socialShareTitle={socialShareTitle}
        socialShareDescription={socialShareDescription}
        url={person.url}
        image={image}
        person={person}
        families={families}
      />

      <PersonPageBanner
        person={person}
        analytics={{ ...analytics, link_page: `${linkPage}: Header` }}
        displaySocial={!CF}
        showRelationship={showRelationship}
      />

      <div css={centeredCss}>
        <PersonMasonryLayout>
          {lifeOf && <LifeOfCard data-testid="life-of-card" person={person} {...lifeOf} />}

          <MemoriesCard
            data-testid="memories-card"
            memories={memories?.memoryArtifacts?.slice(0, 6)}
            memoryCount={memories?.memoriesCount || 0}
            person={person}
            baseUrl={baseUrl}
            analytics={analytics}
            loggedIn={signedIn}
            restricted={CF}
          />

          {!CF && (
            <ActivitiesCard data-testid="activities-card" person={person} analytics={analytics} baseUrl={baseUrl} />
          )}

          {canDisplayTimeline && (
            <TimelineCard data-testid="timeline-card" timelineItems={timelineItems} analytics={analytics} />
          )}

          {families && families.spouses?.[0] && (
            <FamilyCard
              data-testid="couple-family-card"
              families={families}
              focusFamily={families.spouses?.[0]}
              type="couple"
              focusPersonId={person.id}
              analytics={analytics}
              customPersonLinkOrOverlay={NotPublished}
            />
          )}

          {families && families.parents?.[0] && (
            <FamilyCard
              data-testid="parents-family-card"
              families={families}
              focusFamily={families.parents?.[0]}
              type="parents"
              focusPersonId={person.id}
              analytics={analytics}
              customPersonLinkOrOverlay={NotPublished}
            />
          )}

          {worldEvents && worldEvents.length > 0 && (
            <WorldEventsCard
              data-testid="world-events-card"
              events={worldEvents}
              person={person}
              analytics={analytics}
            />
          )}

          {nameMeanings?.length && (
            <NameMeaningCard data-testid="name-meaning-card" meanings={nameMeanings} analytics={analytics} />
          )}

          {popularStory && (
            <StoryCard
              data-testid="story-card"
              data={popularStory}
              person={person}
              baseUrl={baseUrl}
              analytics={analytics}
              loggedIn={signedIn}
              restricted={CF}
            />
          )}

          <SourcesCard
            data-testid="sources-card"
            sources={sources?.sources}
            sourcesCount={sources?.sourcesCount}
            person={person}
            analytics={analytics}
            restricted={CF}
            baseUrl={baseUrl}
          />

          {!signedIn && !CF && (
            <CreateAccountCard data-testid="create-account-card" analytics={analytics} baseUrl={baseUrl} />
          )}
        </PersonMasonryLayout>
      </div>

      {!CF && <AncestorPageFooter person={person} analytics={{ ...analytics, link_page: `${linkPage}: Footer` }} />}
    </>
  )
}
